import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { CONNECT_API_URL } from '../../constants/connect-api-url.token';
import { CONSENT_CONFIG } from '../../constants/consent-config.token';

export interface SignupConsent {
  consentName: string;
  version: string;
  history: string;
}

export interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  country: string;
}

export interface InvitedUser {
  expirationDateTimeUtc: string;
  user: {
    email: string;
    firstName: string;
    lastName: string;
    country: string;
    language: string;
  };
  companyCode: string;
}

export interface InviteAcceptPayload {
  id: string;
  email: string;
  password: string;
  passwordConfirm: string;
  firstName: string;
  lastName: string;
  country: string;
  language: string;
  leadType: string;
  salutation: string;
  acceptTOU: boolean;
  consent: SignupConsent;
  contractCode: string;
}

@Injectable({ providedIn: 'root' })
export class SignupStore {
  private readonly http = inject(HttpClient);
  private readonly baseUrl: string = inject(CONNECT_API_URL);
  private readonly consentConfig = inject(CONSENT_CONFIG);

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private readonly _error = signal<string | null>(null);
  readonly error = this._error.asReadonly();

  private readonly _success = signal(false);
  readonly success = this._success.asReadonly();

  private readonly _invitedUser = signal<InvitedUser | null>(null);
  readonly invitedUser = this._invitedUser.asReadonly();

  private readonly _invitationExpired = signal(false);
  readonly invitationExpired = this._invitationExpired.asReadonly();

  private readonly _invitationInvalid = signal(false);
  readonly invitationInvalid = this._invitationInvalid.asReadonly();

  validateInvitation(code: string): Observable<InvitedUser> {
    this._loading.set(true);
    this._error.set(null);
    this._invitedUser.set(null);
    this._invitationExpired.set(false);
    this._invitationInvalid.set(false);

    return this.http.get<InvitedUser>(`${this.baseUrl}registration/${code}`).pipe(
      tap({
        next: response => {
          const expiration = new Date(response.expirationDateTimeUtc);
          if (expiration < new Date()) {
            this._invitationExpired.set(true);
          } else {
            this._invitedUser.set(response);
          }
          this._loading.set(false);
        },
        error: () => {
          this._invitationInvalid.set(true);
          this._loading.set(false);
        },
      })
    );
  }

  signUp(formData: SignupFormData, language: string, invitationCode: string): Observable<unknown> {
    this._loading.set(true);
    this._error.set(null);
    this._success.set(false);

    const payload: InviteAcceptPayload = {
      id: invitationCode,
      email: formData.email,
      password: formData.password,
      passwordConfirm: formData.confirmPassword,
      firstName: formData.firstName,
      lastName: formData.lastName,
      country: formData.country,
      language,
      leadType: 'Other',
      salutation: 'D',
      acceptTOU: true,
      consent: {
        consentName: 'tos',
        version: this.consentConfig.version,
        history: '',
      },
      contractCode: 'C300100004',
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    return this.http
      .post(`${this.baseUrl}registration/invite_in/accept`, payload, { headers })
      .pipe(
        tap({
          next: () => {
            this._loading.set(false);
            this._success.set(true);
          },
          error: err => {
            this._loading.set(false);
            this._error.set(err?.error?.message ?? 'Registration failed');
          },
        })
      );
  }
}
